import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe,
  Eye,
  ExternalLink,
  CheckCircle,
  Clock,
  AlertCircle,
  LogOut,
  Loader2,
  Rocket,
  CreditCard,
} from 'lucide-react';
import { PortalFooter } from '../components/PortalFooter';
import { useAuth } from '../App';
import { getSite, formatStatus, getStatusColor, Website, approveSite } from '../api/website';
import { getOnboardingStatus } from '../api/onboarding';
import { logout } from '../api/auth';
import { DomainStatus } from '../components/DomainStatus';
import { EmailForwardingPanel } from '../components/EmailForwardingPanel';
import { ServiceChargesPanel } from '../components/ServiceChargesPanel';
import { getFeatures } from '../api/email-forwarding';
import { createBillingPortalSession } from '../api/billing';

// The onboarding wizard lives on the dashboard app, not the portal.
const ONBOARDING_APP_URL =
  import.meta.env.VITE_ONBOARDING_URL || 'https://onboarding.niftee.ai';

export default function DashboardPage() {
  const { customer, logout: handleLogout } = useAuth();
  const [website, setWebsite] = useState<Website | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [approvalSuccess, setApprovalSuccess] = useState('');
  const [isBillingLoading, setIsBillingLoading] = useState(false);
  const [emailForwardingEnabled, setEmailForwardingEnabled] = useState(false);

  useEffect(() => {
    async function fetchSite() {
      try {
        const { website } = await getSite();

        // No site to review yet? The customer may still be mid-onboarding. The wizard lives
        // on the dashboard app, so send them there instead of stranding them on an empty
        // portal dashboard (a portal link is a dead end before a site exists).
        if ((!website || website.status === 'pending') && customer?.id) {
          const onboarding = await getOnboardingStatus(customer.id);
          if (onboarding?.status === 'in_progress') {
            window.location.replace(
              `${ONBOARDING_APP_URL}/onboarding/start?customer_id=${customer.id}`
            );
            return; // navigating away — keep the spinner up
          }
        }

        setWebsite(website);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load website');
      }
      setIsLoading(false);
    }

    fetchSite();
  }, [customer?.id]);

  // Portal feature flags — gate optional tools (e.g. email forwarding). The
  // server enforces the same flag, so this only controls visibility.
  useEffect(() => {
    getFeatures()
      .then((features) => setEmailForwardingEnabled(!!features.email_forwarding))
      .catch(() => setEmailForwardingEnabled(false));
  }, [customer?.id]);

  const handleSignOut = async () => {
    try {
      await logout();
    } catch {
      // Ignore errors, still clear local session
    }
    handleLogout();
  };

  const handleApprove = async () => {
    if (isApproving) return;
    setIsApproving(true);
    setError('');
    setApprovalSuccess('');

    try {
      const result = await approveSite();
      setApprovalSuccess(result.message);
      // Refresh website data to show new status
      const { website: updated } = await getSite();
      setWebsite(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve site');
    } finally {
      setIsApproving(false);
    }
  };

  const handleManageBilling = async () => {
    if (isBillingLoading) return;
    setIsBillingLoading(true);
    try {
      const portalUrl = await createBillingPortalSession();
      window.location.href = portalUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
      setIsBillingLoading(false);
    }
  };

  // Determine what actions are available based on status
  const isReadyForReview = website?.status === 'pending-customer-review';
  // /preview iframes website.cloudflare_url — without one there is nothing to
  // render, so the card would link straight to "No preview available".
  const hasDeployedPreview = !!website?.cloudflare_url;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved-to-launch':
      case 'live':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending-customer-review':
        return <AlertCircle className="w-5 h-5 text-blue-600" />;
      case 'needs-customer-revision':
        return <Clock className="w-5 h-5 text-orange-600" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold">
                <span className="text-primary-500">niftee</span>
                <span className="text-accent-500">.</span>
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">{customer?.email}</span>
              <button
                onClick={handleSignOut}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Sign out"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">
            Welcome{customer?.businessName ? `, ${customer.businessName}` : ''}!
          </h2>
          <p className="text-gray-600 mt-1">
            Here's an overview of your website.
          </p>
        </div>

        {approvalSuccess && (
          <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">{approvalSuccess}</p>
              <p className="text-sm mt-1">Your website is now live and accessible to visitors.</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">{error}</div>
        )}

        {!website ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No website found
            </h3>
            <p className="text-gray-600">
              Your website is being created. Check back soon!
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    {getStatusIcon(website.status)}
                    <h3 className="text-xl font-semibold text-gray-900">
                      {website.business_name}
                    </h3>
                  </div>
                  <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(website.status)}`}>
                    {formatStatus(website.status)}
                  </span>
                </div>
                {website.cloudflare_url && (
                  <a
                    href={website.cloudflare_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Visit Site
                  </a>
                )}
              </div>

              {isReadyForReview && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-blue-800 font-medium">
                    Your website is ready for review!
                  </p>
                  <p className="text-blue-700 text-sm mt-1">
                    Preview your site and let us know if you need any changes, or approve it when you're ready.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Link
                      to="/edit"
                      className="px-4 py-2 bg-white text-blue-700 border border-blue-200 font-medium rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      Preview, Edit &amp; Request Revisions
                    </Link>
                    <button
                      onClick={handleApprove}
                      disabled={isApproving}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isApproving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Approving...
                        </>
                      ) : (
                        <>
                          <Rocket className="w-4 h-4" />
                          Approve My Website
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {website.status === 'needs-customer-revision' && (
                <div className="mt-4 p-4 bg-orange-50 rounded-lg">
                  <p className="text-orange-800 font-medium">
                    Revision in progress
                  </p>
                  <p className="text-orange-700 text-sm mt-1">
                    Our team is working on your requested changes. We'll notify you when they're complete.
                  </p>
                </div>
              )}
            </div>

            {/*
              Action cards are conditional — both previously led customers to
              dead ends before launch.

              Preview: /preview iframes `website.cloudflare_url`, which is null
              until a site is deployed, so the card rendered "No preview
              available". It is also the ONLY route into the editor after
              launch (the blue panel above is gated to pending-customer-review),
              so it must stay visible once a deployment exists — never remove it
              outright.

              Request Changes: hidden from the dashboard. The flow is not gone —
              the customer editor has its own Request Changes control that
              routes to /revisions, and that is the sensible place to ask for
              changes, with the site in front of you.
            */}
            {hasDeployedPreview && (
              <div className="grid gap-6">
                {/* Preview Card */}
                <Link
                  to="/preview"
                  className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow group"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors">
                      <Eye className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Preview &amp; Edit Website</h3>
                      <p className="text-gray-600 text-sm">See your site, make edits, or request revisions</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">
                    View your website on desktop and mobile, edit your content, or send us a revision request — all from one place.
                  </p>
                </Link>
              </div>
            )}

            {/* Site Info */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Site Details</h3>
              <dl className="grid sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm text-gray-500">Website URL</dt>
                  <dd className="text-gray-900 mt-1">
                    {website.custom_domain || website.cloudflare_url || 'Not deployed yet'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Build Version</dt>
                  <dd className="text-gray-900 mt-1 capitalize">
                    {website.template_used || 'Default'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Created</dt>
                  <dd className="text-gray-900 mt-1">
                    {new Date(website.created_at).toLocaleDateString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Last Updated</dt>
                  <dd className="text-gray-900 mt-1">
                    {new Date(website.last_updated).toLocaleDateString()}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Plan & Billing */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Plan & Billing</h3>
                {customer?.hasBillingAccount && (
                  <button
                    onClick={handleManageBilling}
                    disabled={isBillingLoading}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isBillingLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4" />
                    )}
                    Manage Billing
                  </button>
                )}
              </div>
              <dl className="grid sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm text-gray-500">Current Plan</dt>
                  <dd className="text-gray-900 mt-1 capitalize font-medium">
                    {customer?.planTier || 'Free'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Billing</dt>
                  <dd className="text-gray-900 mt-1 capitalize">
                    {customer?.billingFrequency || '—'}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Domain Management */}
            {['pending-customer-review', 'approved-to-launch', 'active'].includes(website.status) && (
              <DomainStatus />
            )}

            {/* Email Forwarding — only when enabled for this customer and a
                custom domain is connected (forwarding needs their own domain). */}
            {emailForwardingEnabled && customer?.customDomain && <EmailForwardingPanel />}

            {/* One-time service fees. Self-hiding when the customer has none,
                so it needs no gating condition here. */}
            <ServiceChargesPanel />
          </div>
        )}
      </main>

      <PortalFooter />
    </div>
  );
}
